import React, { FC, ReactNode } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
} from '@material-ui/core'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import HandleMarkup from '@src/components/HandleMarkup'
import LoadingBox from '@src/components/LoadingBox'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '@utils/messages'
import { useQuickOrder } from '@src/components/QuickOrder/utils/quickOrderContext'
import { lineClamp } from '@src/styles/utils/lineClamp'
import { times } from 'lodash'
import clsx from 'clsx'

const useStyles = makeStyles((theme: Theme) => ({
  loadingBox: {
    marginRight: 0,
  },
  skuCell: {
    alignItems: 'center',
    whiteSpace: 'nowrap',
    padding: theme.spacing(0, 0, 0, 1),
    '& > label': {
      alignItems: 'center',
      verticalAlign: 'middle',
    },
    '& > label > span': {
      marginTop: 0,
      lineHeight: 1.46,
    },
  },
  name: {
    ...lineClamp(2),
  },
  tableContainer: {
    borderBottom: `1px solid ${theme.palette.grey['300']}`,
    maxHeight: '20rem',
    marginBottom: '.5rem',
  },
  tableHeadCell: {
    paddingLeft: theme.spacing(7),
    width: '10rem',
  },
  tableHeadRow: {
    whiteSpace: 'nowrap',
    fontSize: theme.typography.pxToRem(14),
  },
  tableRow: {
    '& .MuiTableCell-body': {
      border: 0,
      verticalAlign: 'baseline',
    },
  },
  tableRowNone: {
    '& .MuiTableCell-body': {
      border: 0,
      opacity: 0.5,
    },
  },
  checkboxSpacingAdjustment: {
    position: 'relative',
    top: theme.spacing(0.75),
  },
}))

export const ItemRow: FC<{
  index: number
  name: string
  number: string
  remainingQuantity: Maybe<number> | undefined
  quantity: Maybe<number> | undefined
  isQuoteExpired: boolean
}> = ({ index, name, number, remainingQuantity, quantity, isQuoteExpired }) => {
  const classes = useStyles()
  const intl = useIntl()

  const noneRemaining = remainingQuantity === 0

  const { pendingItems, updatePendingItems } = useQuickOrder()

  const isChecked = pendingItems.some((item) => item.id === index)

  const handleCheck = () => {
    updatePendingItems(index, number, remainingQuantity ? remainingQuantity : 1)
  }

  return (
    <>
      <TableRow
        classes={{
          root: noneRemaining ? classes.tableRowNone : classes.tableRow,
        }}
      >
        <TableCell className={clsx({ [classes.skuCell]: !isQuoteExpired })}>
          {!isQuoteExpired && (
            <LiquidCheckbox
              name={`${number}-${index}`}
              checked={isChecked}
              onChange={() => handleCheck()}
              disabled={noneRemaining}
              label={
                <Typography variant="body2" component="span">
                  {number}
                </Typography>
              }
            />
          )}
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            component="span"
            className={clsx(classes.name, {
              [classes.checkboxSpacingAdjustment]: !isQuoteExpired,
            })}
          >
            <HandleMarkup value={name} />
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            variant="body2"
            component="span"
            className={clsx({
              [classes.checkboxSpacingAdjustment]: !isQuoteExpired,
            })}
          >
            {intl.formatMessage(messages.QUOTES_REMAINING_QUANTITY, {
              remainingQuantity,
              quantity,
            })}
          </Typography>
        </TableCell>
      </TableRow>
    </>
  )
}

const QuoteItemsTable: FC<{ children: ReactNode }> = ({ children }) => {
  const classes = useStyles()

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow className={classes.tableHeadRow}>
              <TableCell classes={{ root: classes.tableHeadCell }}>
                <FormattedMessage {...messages.SKU} />
              </TableCell>
              <TableCell>
                <FormattedMessage {...messages.NAME} />
              </TableCell>
              <TableCell align="right">
                <FormattedMessage {...messages.QUANTITY_REMAINING} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export const QuoteItemsTableLoadingSkeleton: FC<{}> = () => {
  const classes = useStyles()

  return (
    <QuoteItemsTable>
      {times(4, (index) => (
        <TableRow classes={{ root: classes.tableRow }} key={index}>
          <TableCell className={classes.skuCell}>
            <LoadingBox className={classes.loadingBox} height={24} />
          </TableCell>
          <TableCell>
            <LoadingBox
              className={classes.loadingBox}
              height={24}
              width={200}
            />
          </TableCell>
          <TableCell>
            <LoadingBox className={classes.loadingBox} height={24} />
          </TableCell>
        </TableRow>
      ))}
    </QuoteItemsTable>
  )
}

export default QuoteItemsTable
